import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Loader2, AlertCircle, Search, Filter, ChevronDown, ChevronUp,
  MessageSquare, CheckCircle2, Clock, XCircle, Lightbulb, MessageCircleQuestion,
  BarChart3, Inbox, RefreshCw, UserPlus, User, BookOpen, Plus, Trash2,
  Pin, Eye, ExternalLink, Video, FileText, Upload, Image, Captions,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import supportService, {
  type SupportTicket,
  type SupportAssignee,
  type Suggestion,
  type SupportStats,
  type TrainingMaterial,
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
  SUGGESTION_CATEGORIES,
  SUGGESTION_STATUSES,
  TRAINING_CATEGORIES,
  TRAINING_CONTENT_TYPES,
} from '@/services/api/supportService'
import RichTextEditor from '@/components/ui/RichTextEditor'
import { GUIDANCE_SURFACES } from '@/lib/guidanceSurfaces'
import { useAuth } from '@/providers/AuthProvider'
import toast from 'react-hot-toast'

// Loads a ticket screenshot through the authenticated API client — the endpoint
// needs the Bearer token (and lives on the gateway origin, which PROD's frontend
// nginx doesn't proxy), so a plain <img src="/api/..."> can never render it.
const TicketScreenshot: React.FC<{ ticketId: string }> = ({ ticketId }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    let url: string | null = null
    setObjectUrl(null)
    setFailed(false)
    supportService.getTicketScreenshot(ticketId)
      .then((blob) => {
        url = URL.createObjectURL(blob)
        if (cancelled) return
        setObjectUrl(url)
      })
      .catch((err) => {
        console.error('Failed to load ticket screenshot:', err)
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [ticketId])

  if (failed) {
    return (
      <p className="text-xs text-red-600 flex items-center gap-1">
        <AlertCircle className="w-3.5 h-3.5" />
        Couldn't load the screenshot. Please try again or check the ticket in the database.
      </p>
    )
  }
  if (!objectUrl) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-400 py-4">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading screenshot...
      </div>
    )
  }
  return (
    <img
      src={objectUrl}
      alt="Ticket screenshot"
      className="max-w-full max-h-64 rounded border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
      onClick={() => window.open(objectUrl, '_blank', 'noopener,noreferrer')}
      title="Click to open full size"
    />
  )
}

// Debounce hook for the search boxes
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debouncedValue
}

const SupportAdminPanel: React.FC = () => {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState('tickets')
  const [stats, setStats] = useState<SupportStats | null>(null)

  // Ticket state
  const [tickets, setTickets] = useState<SupportTicket[]>([])
  const [ticketTotal, setTicketTotal] = useState(0)
  const [ticketSearch, setTicketSearch] = useState('')
  const [ticketStatusFilter, setTicketStatusFilter] = useState('')
  const [ticketCategoryFilter, setTicketCategoryFilter] = useState('')
  const [ticketPriorityFilter, setTicketPriorityFilter] = useState('')
  const [expandedTicket, setExpandedTicket] = useState<string | null>(null)

  // Suggestion state
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [suggestionTotal, setSuggestionTotal] = useState(0)
  const [suggestionSearch, setSuggestionSearch] = useState('')
  const [suggestionStatusFilter, setSuggestionStatusFilter] = useState('')
  const [suggestionCategoryFilter, setSuggestionCategoryFilter] = useState('')
  const [expandedSuggestion, setExpandedSuggestion] = useState<string | null>(null)

  // Training state
  const [trainingMaterials, setTrainingMaterials] = useState<TrainingMaterial[]>([])
  const [trainingTotal, setTrainingTotal] = useState(0)
  const [trainingSearch, setTrainingSearch] = useState('')
  const [trainingCategoryFilter, setTrainingCategoryFilter] = useState('')
  const [trainingPublishedFilter, setTrainingPublishedFilter] = useState('')
  const [expandedTraining, setExpandedTraining] = useState<string | null>(null)
  const [showTrainingForm, setShowTrainingForm] = useState(false)
  const [editingTraining, setEditingTraining] = useState<TrainingMaterial | null>(null)

  // Training form state
  const [tTitle, setTTitle] = useState('')
  const [tDescription, setTDescription] = useState('')
  const [tContentHtml, setTContentHtml] = useState('')
  const [tCategory, setTCategory] = useState('getting_started')
  const [tContentType, setTContentType] = useState('article')
  const [tExternalUrl, setTExternalUrl] = useState('')
  const [tVideoUrl, setTVideoUrl] = useState('')
  const [tSurfaceKey, setTSurfaceKey] = useState('')  // '' = library only (no in-context icon)
  const [tIsPublished, setTIsPublished] = useState(false)
  const [tIsPinned, setTIsPinned] = useState(false)
  const [tFile, setTFile] = useState<File | null>(null)
  const [tVideoFile, setTVideoFile] = useState<File | null>(null)
  const [tRemoveVideo, setTRemoveVideo] = useState(false)
  const [tRemoveFile, setTRemoveFile] = useState(false)
  const [tCaptionFile, setTCaptionFile] = useState<File | null>(null)
  const [tRemoveCaption, setTRemoveCaption] = useState(false)

  // `loading` covers the panel's first load only; `refreshing` the Refresh
  // button. Neither replaces the panel once it is up (HR UAT #12, Oct 2026):
  // every keystroke in a search box used to reload all five lists behind a
  // full-panel spinner, which unmounted the search box mid-word.
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')

  // A search runs once typing pauses
  const debouncedTicketSearch = useDebounce(ticketSearch, 300)
  const debouncedSuggestionSearch = useDebounce(suggestionSearch, 300)
  const debouncedTrainingSearch = useDebounce(trainingSearch, 300)
  // The latest load of each list; an older answer arriving late is dropped
  const latestLoad = useRef({ tickets: 0, suggestions: 0, training: 0 })

  // Admin action state
  const [editingTicket, setEditingTicket] = useState<string | null>(null)
  const [ticketNotes, setTicketNotes] = useState('')
  const [ticketResolution, setTicketResolution] = useState('')
  const [ticketNewStatus, setTicketNewStatus] = useState('')
  const [ticketAssignee, setTicketAssignee] = useState('')

  const [editingSuggestion, setEditingSuggestion] = useState<string | null>(null)
  const [suggestionResponse, setSuggestionResponse] = useState('')
  const [suggestionNewStatus, setSuggestionNewStatus] = useState('')

  const [saving, setSaving] = useState(false)

  // Claim/Reassign state
  const [assignees, setAssignees] = useState<SupportAssignee[]>([])
  const [showReassign, setShowReassign] = useState<string | null>(null)
  const [reassignTarget, setReassignTarget] = useState<string>('')

  const loadStats = useCallback(async () => {
    try {
      const data = await supportService.getStats()
      setStats(data)
    } catch { /* ignore */ }
  }, [])

  const loadTickets = useCallback(async () => {
    const load = ++latestLoad.current.tickets
    try {
      const params: Record<string, string> = {}
      if (ticketStatusFilter) params.status = ticketStatusFilter
      if (ticketCategoryFilter) params.category = ticketCategoryFilter
      if (ticketPriorityFilter) params.priority = ticketPriorityFilter
      if (debouncedTicketSearch) params.search = debouncedTicketSearch
      const data = await supportService.getAllTickets(params)
      if (load !== latestLoad.current.tickets) return
      setTickets(data.tickets || [])
      setTicketTotal(data.total || 0)
    } catch {
      setError('Failed to load tickets')
    }
  }, [ticketStatusFilter, ticketCategoryFilter, ticketPriorityFilter, debouncedTicketSearch])

  const loadSuggestions = useCallback(async () => {
    const load = ++latestLoad.current.suggestions
    try {
      const params: Record<string, string> = {}
      if (suggestionStatusFilter) params.status = suggestionStatusFilter
      if (suggestionCategoryFilter) params.category = suggestionCategoryFilter
      if (debouncedSuggestionSearch) params.search = debouncedSuggestionSearch
      const data = await supportService.getAllSuggestions(params)
      if (load !== latestLoad.current.suggestions) return
      setSuggestions(data.suggestions || [])
      setSuggestionTotal(data.total || 0)
    } catch {
      setError('Failed to load suggestions')
    }
  }, [suggestionStatusFilter, suggestionCategoryFilter, debouncedSuggestionSearch])

  const loadAssignees = useCallback(async () => {
    try {
      const data = await supportService.getAssignees()
      setAssignees(data)
    } catch {
      console.error('Failed to load assignees')
    }
  }, [])

  const loadTraining = useCallback(async () => {
    const load = ++latestLoad.current.training
    try {
      const params: Record<string, string | boolean> = {}
      if (trainingCategoryFilter) params.category = trainingCategoryFilter
      if (trainingPublishedFilter === 'true') params.published = true
      if (trainingPublishedFilter === 'false') params.published = false
      if (debouncedTrainingSearch) params.search = debouncedTrainingSearch
      const data = await supportService.getAllTraining(params as any)
      if (load !== latestLoad.current.training) return
      setTrainingMaterials(data.materials || [])
      setTrainingTotal(data.total || 0)
    } catch {
      setError('Failed to load training materials')
    }
  }, [trainingCategoryFilter, trainingPublishedFilter, debouncedTrainingSearch])

  // Once the panel is up, a search or filter change reloads only its own
  // list, in place
  const panelLoaded = useRef(false)
  useEffect(() => {
    if (panelLoaded.current) loadTickets()
  }, [loadTickets])
  useEffect(() => {
    if (panelLoaded.current) loadSuggestions()
  }, [loadSuggestions])
  useEffect(() => {
    if (panelLoaded.current) loadTraining()
  }, [loadTraining])

  // The panel's first load, behind the full spinner
  useEffect(() => {
    const load = async () => {
      setLoading(true)
      await Promise.all([loadStats(), loadTickets(), loadSuggestions(), loadAssignees(), loadTraining()])
      setLoading(false)
      panelLoaded.current = true
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once; later changes reload one list above
  }, [])

  const handleUpdateTicket = async (ticketId: string) => {
    setSaving(true)
    try {
      // Find the selected assignee's user_id if an assignee was selected from dropdown
      const selectedAssignee = assignees.find(a => a.display_name === ticketAssignee)
      await supportService.updateTicket(ticketId, {
        status: ticketNewStatus || undefined,
        admin_notes: ticketNotes || undefined,
        resolution_notes: ticketResolution || undefined,
        assigned_to_name: ticketAssignee || undefined,
        assigned_to_user_id: selectedAssignee?.user_id || undefined,
      })
      setEditingTicket(null)
      setTicketNotes('')
      setTicketResolution('')
      setTicketNewStatus('')
      setTicketAssignee('')
      await Promise.all([loadTickets(), loadStats()])
    } catch {
      setError('Failed to update ticket')
    } finally {
      setSaving(false)
    }
  }

  const handleClaim = async (ticketId: string) => {
    try {
      // Move to in_progress — backend auto-assigns to current user
      await supportService.updateTicket(ticketId, {
        status: 'in_progress',
      })
      await Promise.all([loadTickets(), loadStats()])
      toast.success('Ticket claimed successfully')
    } catch {
      toast.error('Failed to claim ticket')
    }
  }

  const handleReassign = async (ticketId: string) => {
    if (!reassignTarget) return
    const targetUserId = parseInt(reassignTarget)
    const targetUser = assignees.find(a => a.user_id === targetUserId)

    try {
      await supportService.updateTicket(ticketId, {
        status: 'in_progress',
        assigned_to_user_id: targetUserId,
        assigned_to_name: targetUser?.display_name,
      })
      await Promise.all([loadTickets(), loadStats()])
      toast.success(`Ticket reassigned to ${targetUser?.display_name}`)
      setShowReassign(null)
      setReassignTarget('')
    } catch {
      toast.error('Failed to reassign ticket')
    }
  }

  const handleUpdateSuggestion = async (suggestionId: string) => {
    setSaving(true)
    try {
      await supportService.updateSuggestion(suggestionId, {
        status: suggestionNewStatus || undefined,
        admin_response: suggestionResponse || undefined,
      })
      setEditingSuggestion(null)
      setSuggestionResponse('')
      setSuggestionNewStatus('')
      await Promise.all([loadSuggestions(), loadStats()])
    } catch {
      setError('Failed to update suggestion')
    } finally {
      setSaving(false)
    }
  }

  // Training Material handlers
  const resetTrainingForm = () => {
    setTTitle(''); setTDescription(''); setTContentHtml(''); setTCategory('getting_started')
    setTContentType('article'); setTExternalUrl(''); setTVideoUrl('')
    setTIsPublished(false); setTIsPinned(false); setTFile(null); setTVideoFile(null); setTRemoveVideo(false); setTRemoveFile(false)
    setTCaptionFile(null); setTRemoveCaption(false)
    setTSurfaceKey('')
    setEditingTraining(null); setShowTrainingForm(false)
  }

  const startEditTrainingMaterial = (material: TrainingMaterial) => {
    setEditingTraining(material)
    setTTitle(material.title)
    setTDescription(material.description || '')
    setTContentHtml(material.content_html || '')
    setTCategory(material.category)
    setTContentType(material.content_type)
    setTExternalUrl(material.external_url || '')
    setTVideoUrl(material.video_url || '')
    setTSurfaceKey(material.surface_key || '')
    setTIsPublished(material.is_published)
    setTIsPinned(material.is_pinned)
    setTFile(null)
    setTVideoFile(null)
    setTRemoveVideo(false)
    setTRemoveFile(false)
    setTCaptionFile(null)
    setTRemoveCaption(false)
    setShowTrainingForm(true)
  }

  const handleSaveTraining = async () => {
    if (!tTitle.trim()) { toast.error('Title is required'); return }
    if (!tCategory) { toast.error('Category is required'); return }
    setSaving(true)
    try {
      if (editingTraining) {
        await supportService.updateTrainingMaterial(editingTraining.material_id, {
          title: tTitle, description: tDescription, content_html: tContentHtml,
          category: tCategory, content_type: tContentType,
          external_url: tExternalUrl || undefined, video_url: tVideoUrl,
          surface_key: tSurfaceKey,  // '' clears the binding
          is_published: tIsPublished, is_pinned: tIsPinned,
          attachment: tFile || undefined,
          video_file: tVideoFile || undefined,
          caption_file: tCaptionFile || undefined,
          remove_video: tRemoveVideo || undefined,
          remove_file: tRemoveFile || undefined,
          remove_caption: tRemoveCaption || undefined,
        })
        toast.success('Training material updated')
      } else {
        await supportService.createTrainingMaterial({
          title: tTitle, description: tDescription, content_html: tContentHtml,
          category: tCategory, content_type: tContentType,
          external_url: tExternalUrl || undefined, video_url: tVideoUrl || undefined,
          surface_key: tSurfaceKey || undefined,
          is_published: tIsPublished, is_pinned: tIsPinned,
          attachment: tFile || undefined,
          video_file: tVideoFile || undefined,
          caption_file: tCaptionFile || undefined,
        })
        toast.success('Training material created')
      }
      resetTrainingForm()
      await Promise.all([loadTraining(), loadStats()])
    } catch {
      toast.error('Failed to save training material')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteTraining = async (materialId: string) => {
    if (!window.confirm('Are you sure you want to delete this training material?')) return
    try {
      await supportService.deleteTrainingMaterial(materialId)
      toast.success('Training material deleted')
      await Promise.all([loadTraining(), loadStats()])
    } catch {
      toast.error('Failed to delete training material')
    }
  }

  const startEditTicket = (ticket: SupportTicket) => {
    setEditingTicket(ticket.ticket_id)
    setTicketNotes(ticket.admin_notes || '')
    setTicketResolution(ticket.resolution_notes || '')
    setTicketNewStatus(ticket.status)
    setTicketAssignee(ticket.assigned_to_name || '')
    setExpandedTicket(ticket.ticket_id)
  }

  const startEditSuggestion = (suggestion: Suggestion) => {
    setEditingSuggestion(suggestion.suggestion_id)
    setSuggestionResponse(suggestion.admin_response || '')
    setSuggestionNewStatus(suggestion.status)
    setExpandedSuggestion(suggestion.suggestion_id)
  }

  const getLabelForValue = (value: string, list: { value: string; label: string }[]) =>
    list.find(i => i.value === value)?.label || value.replace(/_/g, ' ')

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  }

  const getStatusBadge = (status: string) => {
    const config: Record<string, string> = {
      pending: 'bg-amber-100 text-amber-700',
      in_review: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-purple-100 text-purple-700',
      resolved: 'bg-green-100 text-green-700',
      closed: 'bg-gray-100 text-gray-600',
      planned: 'bg-indigo-100 text-indigo-700',
      implemented: 'bg-green-100 text-green-700',
      declined: 'bg-red-100 text-red-600',
    }
    return (
      <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-full ${config[status] || config.pending}`}>
        {status.replace(/_/g, ' ').toUpperCase()}
      </span>
    )
  }

  const getPriorityBadge = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'bg-green-50 text-green-700', medium: 'bg-amber-50 text-amber-700',
      high: 'bg-orange-50 text-orange-700', urgent: 'bg-red-50 text-red-700',
    }
    return (
      <span className={`text-[11px] font-medium px-2 py-0.5 rounded ${colors[priority] || colors.medium}`}>
        {priority.toUpperCase()}
      </span>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-16">
        <Loader2 className="w-8 h-8 animate-spin text-[#29ABE2]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white border rounded-lg p-4">
            <div className="flex items-center gap-2 text-amber-600 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-xs font-medium">Pending Tickets</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.pending_tickets}</p>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <div className="flex items-center gap-2 text-purple-600 mb-1">
              <Loader2 className="w-4 h-4" />
              <span className="text-xs font-medium">In Progress</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.in_progress_tickets}</p>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <div className="flex items-center gap-2 text-green-600 mb-1">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-xs font-medium">Resolved</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.resolved_tickets}</p>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <Lightbulb className="w-4 h-4" />
              <span className="text-xs font-medium">Suggestions</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.total_suggestions}</p>
            <p className="text-xs text-gray-500">{stats.implemented_suggestions} implemented</p>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <div className="flex items-center gap-2 text-indigo-600 mb-1">
              <BookOpen className="w-4 h-4" />
              <span className="text-xs font-medium">Training</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.total_training_materials}</p>
            <p className="text-xs text-gray-500">{stats.published_training_materials} published</p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-4 py-3 rounded-lg">
          <AlertCircle className="w-4 h-4" />
          {error}
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <TabsList>
            <TabsTrigger value="tickets" className="flex items-center gap-1.5">
              <MessageCircleQuestion className="w-4 h-4" />
              Tickets ({ticketTotal})
            </TabsTrigger>
            <TabsTrigger value="suggestions" className="flex items-center gap-1.5">
              <Lightbulb className="w-4 h-4" />
              Suggestions ({suggestionTotal})
            </TabsTrigger>
            <TabsTrigger value="training" className="flex items-center gap-1.5">
              <BookOpen className="w-4 h-4" />
              Training ({trainingTotal})
            </TabsTrigger>
          </TabsList>
          <Button
            variant="outline"
            size="sm"
            disabled={refreshing}
            onClick={async () => {
              setRefreshing(true)
              await Promise.all([loadStats(), loadTickets(), loadSuggestions(), loadTraining()])
              setRefreshing(false)
            }}
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* ===== TICKETS TAB ===== */}
        <TabsContent value="tickets" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search tickets..."
                value={ticketSearch}
                onChange={e => setTicketSearch(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
            <Select value={ticketStatusFilter} onValueChange={v => setTicketStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[140px] h-9"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {TICKET_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={ticketCategoryFilter} onValueChange={v => setTicketCategoryFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[140px] h-9"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {TICKET_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={ticketPriorityFilter} onValueChange={v => setTicketPriorityFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[120px] h-9"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {TICKET_PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Ticket List */}
          {tickets.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Inbox className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No tickets found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {tickets.map(ticket => {
                const isExpanded = expandedTicket === ticket.ticket_id
                const isEditing = editingTicket === ticket.ticket_id

                return (
                  <div key={ticket.ticket_id} className="border rounded-lg bg-white">
                    <button
                      onClick={() => setExpandedTicket(isExpanded ? null : ticket.ticket_id)}
                      className="w-full p-4 flex items-center gap-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-xs font-mono text-gray-400">{ticket.ticket_id}</span>
                          {getPriorityBadge(ticket.priority)}
                          {getStatusBadge(ticket.status)}
                        </div>
                        <p className="text-sm font-medium text-gray-900 truncate">{ticket.subject}</p>
                        <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1 flex-wrap">
                          <span>{ticket.submitter_name} · {getLabelForValue(ticket.category, TICKET_CATEGORIES)} · {formatDate(ticket.created_at)}</span>
                          {ticket.assigned_to_name ? (
                            <span className="inline-flex items-center gap-1 text-blue-600 font-medium">
                              <User className="w-3 h-3" />
                              {ticket.assigned_to_name}
                            </span>
                          ) : ticket.status !== 'resolved' && ticket.status !== 'closed' ? (
                            <span className="italic text-gray-400">Unassigned</span>
                          ) : null}
                        </p>
                      </div>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-4 border-t space-y-4 pt-4">
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Description</p>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded">{ticket.description}</p>
                        </div>

                        {ticket.screenshot_url && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                              <Image className="w-3.5 h-3.5" />
                              Screenshot
                              {ticket.screenshot_filename && (
                                <span className="text-gray-400 font-normal">({ticket.screenshot_filename})</span>
                              )}
                            </p>
                            <TicketScreenshot ticketId={ticket.ticket_id} />
                          </div>
                        )}

                        {ticket.current_page && (
                          <p className="text-xs text-gray-500">Page: <code className="bg-gray-100 px-1 rounded">{ticket.current_page}</code></p>
                        )}
                        {ticket.submitter_email && (
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span>Email: {ticket.submitter_email}</span>
                            <a
                              href={`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(ticket.submitter_email)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                              title={`Chat with ${ticket.submitter_name} on Teams`}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#505AC9]/10 text-[#505AC9] hover:bg-[#505AC9]/20 transition-colors font-medium"
                            >
                              <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor">
                                <path d="M9.186 4.797a2.42 2.42 0 1 0-2.86-2.448h1.178c.929 0 1.682.753 1.682 1.682zm-4.295 7.738h2.613c.929 0 1.682-.753 1.682-1.682V5.58h2.783a.7.7 0 0 1 .682.716v4.294a4.197 4.197 0 0 1-4.093 4.293c-1.618-.04-3-.99-3.667-2.35Zm10.737-9.372a1.674 1.674 0 1 1-3.349 0 1.674 1.674 0 0 1 3.349 0m-2.238 9.488-.12-.002a5.2 5.2 0 0 0 .381-2.07V6.306a1.7 1.7 0 0 0-.15-.725h1.792c.39 0 .707.317.707.707v3.765a2.6 2.6 0 0 1-2.598 2.598z"/>
                                <path d="M.682 3.349h6.822c.377 0 .682.305.682.682v6.822a.68.68 0 0 1-.682.682H.682A.68.68 0 0 1 0 10.853V4.03c0-.377.305-.682.682-.682Zm5.206 2.596v-.72h-3.59v.72h1.357V9.66h.87V5.945z"/>
                              </svg>
                              Teams
                            </a>
                          </div>
                        )}

                        {/* Reassign Panel */}
                        {showReassign === ticket.ticket_id && (
                          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <label className="text-xs text-gray-600 mb-2 block">Reassign To</label>
                            <div className="flex items-center gap-2">
                              <Select value={reassignTarget} onValueChange={setReassignTarget}>
                                <SelectTrigger className="flex-1 h-8 text-xs">
                                  <SelectValue placeholder="Select assignee..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {assignees
                                    .filter(a => a.user_id !== ticket.assigned_to_user_id)
                                    .map(a => (
                                      <SelectItem key={a.user_id} value={String(a.user_id)}>
                                        {a.display_name}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                              <Button
                                size="sm"
                                disabled={!reassignTarget}
                                onClick={() => handleReassign(ticket.ticket_id)}
                              >
                                Confirm
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setShowReassign(null); setReassignTarget('') }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Admin Edit Form */}
                        {isEditing ? (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                            <p className="text-sm font-medium text-blue-800">Update Ticket</p>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs text-gray-600">Status</label>
                                <Select value={ticketNewStatus} onValueChange={setTicketNewStatus}>
                                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {TICKET_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <label className="text-xs text-gray-600">Assigned To</label>
                                <Select
                                  value={ticketAssignee}
                                  onValueChange={setTicketAssignee}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue placeholder="Select assignee..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {assignees.map(a => (
                                      <SelectItem key={a.user_id} value={a.display_name}>
                                        {a.display_name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div>
                              <label className="text-xs text-gray-600">Admin Notes</label>
                              <Textarea
                                value={ticketNotes}
                                onChange={e => setTicketNotes(e.target.value)}
                                placeholder="Internal notes..."
                                rows={2}
                                className="text-xs resize-none"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-600">Resolution Notes (visible to user)</label>
                              <Textarea
                                value={ticketResolution}
                                onChange={e => setTicketResolution(e.target.value)}
                                placeholder="Response to the user..."
                                rows={2}
                                className="text-xs resize-none"
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleUpdateTicket(ticket.ticket_id)}
                                disabled={saving}
                                className="bg-[#15305D] hover:bg-[#1a4a8a]"
                              >
                                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                                Save
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingTicket(null)}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-2 flex-wrap">
                            <Button size="sm" variant="outline" onClick={() => startEditTicket(ticket)}>
                              <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                              Respond / Update
                            </Button>

                            {/* Claim button — pending & unassigned */}
                            {(ticket.status === 'pending' || ticket.status === 'in_review') && !ticket.assigned_to_user_id && (
                              <Button
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                onClick={() => handleClaim(ticket.ticket_id)}
                              >
                                <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                                Claim
                              </Button>
                            )}

                            {/* Reassign button — in_progress or in_review with assignee */}
                            {(ticket.status === 'in_progress' || ticket.status === 'in_review') && showReassign !== ticket.ticket_id && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                onClick={() => { setShowReassign(ticket.ticket_id); setReassignTarget('') }}
                              >
                                <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                                Reassign
                              </Button>
                            )}
                          </div>
                        )}

                        {/* Existing admin notes */}
                        {ticket.admin_notes && !isEditing && (
                          <div className="bg-gray-50 rounded p-3">
                            <p className="text-xs font-medium text-gray-500 mb-1">Admin Notes</p>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{ticket.admin_notes}</p>
                          </div>
                        )}
                        {ticket.resolution_notes && !isEditing && (
                          <div className="bg-green-50 border border-green-200 rounded p-3">
                            <p className="text-xs font-medium text-green-700 mb-1">Resolution</p>
                            <p className="text-sm text-green-800 whitespace-pre-wrap">{ticket.resolution_notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </TabsContent>

        {/* ===== SUGGESTIONS TAB ===== */}
        <TabsContent value="suggestions" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search suggestions..."
                value={suggestionSearch}
                onChange={e => setSuggestionSearch(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
            <Select value={suggestionStatusFilter} onValueChange={v => setSuggestionStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[140px] h-9"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {SUGGESTION_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={suggestionCategoryFilter} onValueChange={v => setSuggestionCategoryFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[140px] h-9"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {SUGGESTION_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Suggestion List */}
          {suggestions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Inbox className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No suggestions found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {suggestions.map(suggestion => {
                const isExpanded = expandedSuggestion === suggestion.suggestion_id
                const isEditing = editingSuggestion === suggestion.suggestion_id

                return (
                  <div key={suggestion.suggestion_id} className="border rounded-lg bg-white">
                    <button
                      onClick={() => setExpandedSuggestion(isExpanded ? null : suggestion.suggestion_id)}
                      className="w-full p-4 flex items-center gap-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-xs font-mono text-gray-400">{suggestion.suggestion_id}</span>
                          {getStatusBadge(suggestion.status)}
                          {suggestion.vote_count > 0 && (
                            <Badge variant="outline" className="text-[10px]">
                              {suggestion.vote_count} votes
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm font-medium text-gray-900 truncate">{suggestion.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {suggestion.submitter_name} · {getLabelForValue(suggestion.category, SUGGESTION_CATEGORIES)}
                          {suggestion.impact_area ? ` · ${suggestion.impact_area}` : ''}
                          {' · '}{formatDate(suggestion.created_at)}
                        </p>
                      </div>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-4 border-t space-y-4 pt-4">
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Description</p>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded">{suggestion.description}</p>
                        </div>

                        {suggestion.business_value && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Business Value</p>
                            <p className="text-sm text-gray-700 bg-amber-50 p-3 rounded">{suggestion.business_value}</p>
                          </div>
                        )}

                        {suggestion.submitter_email && (
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span>Email: {suggestion.submitter_email}</span>
                            <a
                              href={`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(suggestion.submitter_email)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                              title={`Chat with ${suggestion.submitter_name} on Teams`}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#505AC9]/10 text-[#505AC9] hover:bg-[#505AC9]/20 transition-colors font-medium"
                            >
                              <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor">
                                <path d="M9.186 4.797a2.42 2.42 0 1 0-2.86-2.448h1.178c.929 0 1.682.753 1.682 1.682zm-4.295 7.738h2.613c.929 0 1.682-.753 1.682-1.682V5.58h2.783a.7.7 0 0 1 .682.716v4.294a4.197 4.197 0 0 1-4.093 4.293c-1.618-.04-3-.99-3.667-2.35Zm10.737-9.372a1.674 1.674 0 1 1-3.349 0 1.674 1.674 0 0 1 3.349 0m-2.238 9.488-.12-.002a5.2 5.2 0 0 0 .381-2.07V6.306a1.7 1.7 0 0 0-.15-.725h1.792c.39 0 .707.317.707.707v3.765a2.6 2.6 0 0 1-2.598 2.598z"/>
                                <path d="M.682 3.349h6.822c.377 0 .682.305.682.682v6.822a.68.68 0 0 1-.682.682H.682A.68.68 0 0 1 0 10.853V4.03c0-.377.305-.682.682-.682Zm5.206 2.596v-.72h-3.59v.72h1.357V9.66h.87V5.945z"/>
                              </svg>
                              Teams
                            </a>
                          </div>
                        )}

                        {/* Admin Edit Form */}
                        {isEditing ? (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                            <p className="text-sm font-medium text-blue-800">Update Suggestion</p>
                            <div>
                              <label className="text-xs text-gray-600">Status</label>
                              <Select value={suggestionNewStatus} onValueChange={setSuggestionNewStatus}>
                                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {SUGGESTION_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="text-xs text-gray-600">Response (visible to user)</label>
                              <Textarea
                                value={suggestionResponse}
                                onChange={e => setSuggestionResponse(e.target.value)}
                                placeholder="Thank them, explain your decision..."
                                rows={3}
                                className="text-xs resize-none"
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleUpdateSuggestion(suggestion.suggestion_id)}
                                disabled={saving}
                                className="bg-[#15305D] hover:bg-[#1a4a8a]"
                              >
                                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                                Save
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingSuggestion(null)}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => startEditSuggestion(suggestion)}>
                              <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                              Respond / Update
                            </Button>
                          </div>
                        )}

                        {suggestion.admin_response && !isEditing && (
                          <div className="bg-green-50 border border-green-200 rounded p-3">
                            <p className="text-xs font-medium text-green-700 mb-1">Admin Response</p>
                            <p className="text-sm text-green-800 whitespace-pre-wrap">{suggestion.admin_response}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </TabsContent>
        {/* ===== TRAINING TAB ===== */}
        <TabsContent value="training" className="space-y-4">
          {/* Create / Edit Form */}
          {showTrainingForm ? (
            <div className="bg-white border rounded-lg p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">
                  {editingTraining ? 'Edit Training Material' : 'Create Training Material'}
                </h3>
                <Button size="sm" variant="ghost" onClick={resetTrainingForm}>
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Title *</label>
                  <Input value={tTitle} onChange={e => setTTitle(e.target.value)} placeholder="Training material title" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Category *</label>
                  <Select value={tCategory} onValueChange={setTCategory}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TRAINING_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Content Type</label>
                  <Select value={tContentType} onValueChange={setTContentType}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TRAINING_CONTENT_TYPES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Description (short summary)</label>
                <Textarea
                  value={tDescription}
                  onChange={e => setTDescription(e.target.value)}
                  placeholder="Brief description shown in the card preview..."
                  rows={2}
                  className="text-sm resize-none"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Content (rich text)</label>
                <RichTextEditor
                  content={tContentHtml}
                  onChange={setTContentHtml}
                  placeholder="Write your training content here..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Video URL</label>
                  <Input
                    value={tVideoUrl}
                    onChange={e => {
                      let val = e.target.value.trim()
                      // Auto-extract src from pasted <iframe> embed codes
                      const iframeMatch = val.match(/<iframe[^>]+src=["']([^"']+)["']/i)
                      if (iframeMatch) val = iframeMatch[1]
                      setTVideoUrl(val)
                    }}
                    placeholder="https://youtube.com/watch?v=... or paste iframe embed code"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">External URL only (YouTube, Vimeo). Use "Upload Video" below for self-hosted playback.</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">External Link</label>
                  <Input
                    value={tExternalUrl}
                    onChange={e => setTExternalUrl(e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Upload Video</label>
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
                    <Video className="w-3.5 h-3.5" />
                    {tVideoFile ? tVideoFile.name : ((!tRemoveVideo && editingTraining?.video_blob_filename) || 'Choose video')}
                    <input
                      type="file"
                      className="hidden"
                      accept="video/mp4,video/webm,video/ogg,video/quicktime,.mp4,.webm,.ogv,.mov"
                      onChange={e => { setTVideoFile(e.target.files?.[0] || null); if (e.target.files?.[0]) setTRemoveVideo(false) }}
                    />
                  </label>
                  {(tVideoFile || (editingTraining?.video_blob_filename && !tRemoveVideo)) && (
                    <button onClick={() => { setTVideoFile(null); setTRemoveVideo(true) }} className="text-xs text-red-500 hover:text-red-700">
                      Remove
                    </button>
                  )}
                  {tRemoveVideo && !tVideoFile && editingTraining?.video_blob_filename && (
                    <span className="text-xs text-amber-600">Video will be removed on save</span>
                  )}
                </div>
                <p className="text-[10px] text-gray-400 mt-1">MP4, WebM, OGG, or MOV (max 500MB). Plays directly — no SharePoint login needed.</p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Subtitles / Captions (optional)</label>
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
                    <Captions className="w-3.5 h-3.5" />
                    {tCaptionFile ? tCaptionFile.name : ((!tRemoveCaption && editingTraining?.caption_filename) || 'Choose .vtt')}
                    <input
                      type="file"
                      className="hidden"
                      accept="text/vtt,.vtt"
                      onChange={e => { setTCaptionFile(e.target.files?.[0] || null); if (e.target.files?.[0]) setTRemoveCaption(false) }}
                    />
                  </label>
                  {(tCaptionFile || (editingTraining?.caption_filename && !tRemoveCaption)) && (
                    <button onClick={() => { setTCaptionFile(null); setTRemoveCaption(true) }} className="text-xs text-red-500 hover:text-red-700">
                      Remove
                    </button>
                  )}
                  {tRemoveCaption && !tCaptionFile && editingTraining?.caption_filename && (
                    <span className="text-xs text-amber-600">Captions will be removed on save</span>
                  )}
                </div>
                <p className="text-[10px] text-gray-400 mt-1">WebVTT (.vtt) only. Renders as the subtitle track on the uploaded video. The demo pipeline emits a matching .vtt per section.</p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">In-context guidance surface (optional)</label>
                <select
                  value={tSurfaceKey}
                  onChange={e => setTSurfaceKey(e.target.value)}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-heritage-blue/40"
                >
                  <option value="">— Library only (no page icon) —</option>
                  {GUIDANCE_SURFACES.map(s => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
                <p className="text-[10px] text-gray-400 mt-1">Bind this video to a page/dialog so a "Watch" icon appears there. Only one published video per surface.</p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">File Attachment</label>
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                    <Upload className="w-3.5 h-3.5" />
                    {tFile ? tFile.name : ((!tRemoveFile && editingTraining?.file_filename) || 'Choose file')}
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.webp"
                      onChange={e => { setTFile(e.target.files?.[0] || null); if (e.target.files?.[0]) setTRemoveFile(false) }}
                    />
                  </label>
                  {(tFile || (editingTraining?.file_filename && !tRemoveFile)) && (
                    <button
                      onClick={() => { setTFile(null); setTRemoveFile(true) }}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <p className="text-[10px] text-gray-400 mt-1">PDF, Word, Excel, PowerPoint, or images (max 25MB)</p>
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tIsPublished}
                    onChange={e => setTIsPublished(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-xs text-gray-700">Published</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tIsPinned}
                    onChange={e => setTIsPinned(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-xs text-gray-700">Pinned</span>
                </label>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleSaveTraining}
                  disabled={saving || !tTitle.trim()}
                  className="bg-[#15305D] hover:bg-[#1a4a8a]"
                >
                  {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  {editingTraining ? 'Update' : 'Create'}
                </Button>
                <Button variant="outline" onClick={resetTrainingForm}>Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 items-center">
              <Button
                onClick={() => { resetTrainingForm(); setShowTrainingForm(true) }}
                className="bg-[#15305D] hover:bg-[#1a4a8a]"
                size="sm"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                New Training Material
              </Button>
            </div>
          )}

          {/* Filters */}
          {!showTrainingForm && (
            <div className="flex flex-wrap gap-2">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search training materials..."
                  value={trainingSearch}
                  onChange={e => setTrainingSearch(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>
              <Select value={trainingPublishedFilter} onValueChange={v => setTrainingPublishedFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[140px] h-9"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="true">Published</SelectItem>
                  <SelectItem value="false">Drafts</SelectItem>
                </SelectContent>
              </Select>
              <Select value={trainingCategoryFilter} onValueChange={v => setTrainingCategoryFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[160px] h-9"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {TRAINING_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Training List */}
          {!showTrainingForm && (
            trainingMaterials.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Inbox className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No training materials found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {trainingMaterials.map(material => {
                  const isExpanded = expandedTraining === material.material_id
                  const catLabel = TRAINING_CATEGORIES.find(c => c.value === material.category)?.label || material.category
                  const ctLabel = TRAINING_CONTENT_TYPES.find(c => c.value === material.content_type)?.label || material.content_type

                  return (
                    <div key={material.material_id} className="border rounded-lg bg-white">
                      <button
                        onClick={() => setExpandedTraining(isExpanded ? null : material.material_id)}
                        className="w-full p-4 flex items-center gap-4 text-left hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="text-xs font-mono text-gray-400">{material.material_id}</span>
                            {material.is_published ? (
                              <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">PUBLISHED</span>
                            ) : (
                              <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">DRAFT</span>
                            )}
                            {material.is_pinned && (
                              <Pin className="w-3 h-3 text-amber-500" />
                            )}
                            <Badge variant="outline" className="text-[10px]">{catLabel}</Badge>
                            <Badge variant="outline" className="text-[10px]">{ctLabel}</Badge>
                          </div>
                          <p className="text-sm font-medium text-gray-900 truncate">{material.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {material.created_by_name} · {formatDate(material.created_at)}
                            <span className="ml-2 inline-flex items-center gap-0.5 text-gray-400">
                              <Eye className="w-3 h-3" /> {material.view_count}
                            </span>
                          </p>
                        </div>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </button>

                      {isExpanded && (
                        <div className="px-4 pb-4 border-t space-y-3 pt-4">
                          {material.description && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1">Description</p>
                              <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">{material.description}</p>
                            </div>
                          )}
                          {material.content_html && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1">Content</p>
                              <div
                                className="prose prose-sm max-w-none bg-gray-50 p-3 rounded text-gray-700"
                                dangerouslySetInnerHTML={{ __html: material.content_html }}
                              />
                            </div>
                          )}
                          {material.video_url && (
                            <p className="text-xs text-gray-500 flex items-center gap-1">
                              <Video className="w-3.5 h-3.5" /> Video: <a href={material.video_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline truncate">{material.video_url}</a>
                            </p>
                          )}
                          {material.external_url && (
                            <p className="text-xs text-gray-500 flex items-center gap-1">
                              <ExternalLink className="w-3.5 h-3.5" /> Link: <a href={material.external_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline truncate">{material.external_url}</a>
                            </p>
                          )}
                          {material.file_filename && (
                            <p className="text-xs text-gray-500 flex items-center gap-1">
                              <FileText className="w-3.5 h-3.5" /> File: {material.file_filename}
                              {material.file_size_bytes ? ` (${(material.file_size_bytes / 1024 / 1024).toFixed(1)} MB)` : ''}
                            </p>
                          )}

                          <div className="flex gap-2 pt-2">
                            <Button size="sm" variant="outline" onClick={() => startEditTrainingMaterial(material)}>
                              <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => handleDeleteTraining(material.material_id)}
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default SupportAdminPanel
